/**
 * [PGF Draft Board Refresh v1.1 §D] TraditionalDraftBoard - full render
 * of the physical-draft-board style. Consumed by StandaloneDraftBoard
 * when the resolved board style is "traditional".
 *
 *   • Navy-gradient header row (NOT sticky per verdict 3.A) with yellow
 *     Bebas Neue team names.
 *   • Sticker grid: tight-packed cells, no rounded corners, position-
 *     colored fill with 70%-brightness border, black pill overlays for
 *     position tag + team-bye per reference image refinement.
 *   • Last name centered in Bebas Neue Black clamp(20-32px).
 *   • Winning bid bottom-right.
 *   • Pseudo-random per-sticker rotation/translate seeded by request_id.
 *   • Paper texture: CSS #E8DEC5 base + inline SVG noise at 5% opacity.
 *   • Empty cells rendered as darker cream with 0.5px grid rule.
 *   • Sale animation (§D.9): new picks after mount enter from y=-8 scale=1.05
 *     rot=0 and settle to their seeded resting transform over 400ms ease-out,
 *     with a brief box-shadow flash on landing. Existing picks on mount
 *     render in final state (per §B.4 mid-draft switch rule) - the initial
 *     sale-id set is snapshotted on first render and never animated.
 */
import React, { useRef } from "react";
import { motion } from "framer-motion";
import { TRADITIONAL_POSITION_COLORS, darken70 } from "../../../utils/boardStyles";

const HEADER_HEIGHT = 72;
const CELL_MIN_HEIGHT = 60;

// Deterministic pseudo-random from a string seed (§D.5). Same seed →
// same offset, so re-renders don't wiggle the sticker.
function seededOffset(seed) {
  let h = 2166136261;
  for (let i = 0; i < (seed || "").length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Map to three floats in [-1, 1].
  const rx = (((h >>> 0) % 1000) / 1000) * 2 - 1;
  const ry = ((((h >>> 8) >>> 0) % 1000) / 1000) * 2 - 1;
  const rr = ((((h >>> 16) >>> 0) % 1000) / 1000) * 2 - 1;
  return { tx: rx, ty: ry, rot: rr * 0.4 };
}

function TraditionalSoldCell({ sale, isDark, shouldAnimate, showBid }) {
  const player = sale?.player || {};
  const pos = (player.position || "").toUpperCase();
  const stickerColor = TRADITIONAL_POSITION_COLORS[pos] || "#4B5563";
  const borderColor = darken70(stickerColor);
  const seedId = sale?.request_id || sale?.nomination_id || sale?.sale_id || player.id || player.name || "";
  const { tx, ty, rot } = seededOffset(String(seedId));
  const lastName = ((player.name || "").split(/\s+/).pop() || "").toUpperCase();
  const teamBye = `${(player.nfl_team || "FA").toUpperCase()}-${player.bye_week ?? "?"}`;
  const bid = sale?.paid ?? sale?.winning_bid ?? sale?.final_bid ?? 0;

  // [Stabilization fix - Feb 2026] Reverted to the pre-sweep known-good
  // clamp(20px, 3.2vw, 32px) treatment - the JS useAutoFitText hook this
  // was swapped to measured the sticker's transient grid-cell height
  // (`el.parentElement`, no fixed row height) with a ratio of 34, so
  // maxPx = cellHeight * 34 - hundreds to thousands of px, rendering as
  // an oversized/blown-out glyph (the "white rectangle" look). vw-based
  // clamp has no container to mismeasure - bounded top and bottom,
  // cannot collapse or explode. Hardened with word-break:keep-all /
  // overflow:hidden, which the pre-sweep version didn't have.

  // §D.8 night-mode amber overlay.
  const nightOverlay = isDark
    ? "linear-gradient(rgba(255,180,80,0.06), rgba(255,180,80,0.06))"
    : "none";
  const pillTextShadow = "0 1px 1px rgba(0,0,0,0.35)";

  const stickerContent = (
    <>
      {/* Top-left position - white text directly on the sticker color
          (PGF Draft Board Sweep Item 6, no black pill). */}
      <div
        className="absolute top-0.5 left-1"
        style={{
          color: "#FFFFFF",
          fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
          fontSize: 16,
          lineHeight: 1,
          letterSpacing: "0.04em",
          textShadow: pillTextShadow,
        }}
      >
        {pos}
      </div>
      {/* Top-right team-bye - same white-on-sticker treatment */}
      <div
        className="absolute top-0.5 right-1"
        style={{
          color: "rgba(255,255,255,0.92)",
          fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
          fontSize: 14,
          lineHeight: 1,
          letterSpacing: "0.02em",
          textShadow: pillTextShadow,
        }}
      >
        {teamBye}
      </div>
      {/* Center last name - pre-sweep known-good clamp, hardened to
          never split a word (Stabilization fix, Feb 2026). */}
      <div
        className="uppercase w-full flex items-center justify-center"
        style={{
          fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
          color: "#F5F5F5",
          fontWeight: 900,
          fontSize: "clamp(20px, 3.2vw, 32px)",
          lineHeight: 0.95,
          height: 40,
          letterSpacing: "0.02em",
          textAlign: "center",
          padding: "0 3px",
          overflow: "hidden",
          overflowWrap: "normal",
          wordBreak: "keep-all",
          hyphens: "none",
        }}
      >
        {lastName}
      </div>
      {/* Bottom-right winning bid (auction-specific) */}
      {showBid && (
        <div
          className="absolute bottom-0 right-1"
          style={{
            fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
            color: "rgba(245,245,245,0.9)",
            fontSize: 12,
            lineHeight: 1.2,
            letterSpacing: "0.02em",
          }}
        >
          ${bid}
        </div>
      )}
    </>
  );

  const baseStyle = {
    background: `${nightOverlay}, ${stickerColor}`,
    border: `1px solid ${borderColor}`,
    transformOrigin: "center",
    minHeight: CELL_MIN_HEIGHT,
  };
  const commonClassName =
    "relative w-full h-full flex items-center justify-center overflow-hidden";
  const testId = `traditional-sticker-${seedId}`;

  // §B.4 - existing picks on mount render in final state (no animation).
  if (!shouldAnimate) {
    return (
      <div
        className={commonClassName}
        style={{
          ...baseStyle,
          transform: `translate(${tx}px, ${ty}px) rotate(${rot}deg)`,
        }}
        data-testid={testId}
      >
        {stickerContent}
      </div>
    );
  }

  // §D.9 - sticker-placement animation for new picks only. 400ms settle
  // from y=-8 scale=1.05 rot=0 → seeded resting transform. Box-shadow
  // flashes at moment of landing then fades over 200ms.
  return (
    <motion.div
      className={commonClassName}
      style={baseStyle}
      data-testid={testId}
      initial={{ y: -8, x: 0, scale: 1.05, rotate: 0, boxShadow: "0 0 0 0 rgba(0,0,0,0)" }}
      animate={{
        y: ty,
        x: tx,
        scale: 1,
        rotate: rot,
        boxShadow: [
          "0 0 0 0 rgba(0,0,0,0)",
          "0 0 0 0.5px rgba(0,0,0,0.35)",
          "0 0 0 0 rgba(0,0,0,0)",
        ],
      }}
      transition={{
        duration: 0.4,
        ease: "easeOut",
        boxShadow: { duration: 0.6, times: [0, 0.66, 1], ease: "easeOut" },
      }}
    >
      {stickerContent}
    </motion.div>
  );
}

function TraditionalEmptyCell({ isDark }) {
  const bg = isDark ? "#3A3226" : "#DDD3B8";
  const rule = isDark ? "rgba(0,0,0,0.28)" : "rgba(0,0,0,0.18)";
  return (
    <div
      className="w-full h-full"
      style={{
        background: bg,
        borderTop: `0.5px solid ${rule}`,
        borderRight: `0.5px solid ${rule}`,
        minHeight: CELL_MIN_HEIGHT,
      }}
    />
  );
}

/**
 * Header cell - team name, pre-sweep known-good fixed 28px Bebas Neue
 * (Stabilization fix, Feb 2026 - see file-level note), plus budget under
 * the name, centered, white (auction only via showBudget). PGF Draft
 * Board Sweep Item 6.
 */
function TraditionalHeaderCell({ mgr, showBudget }) {
  const name = (mgr.team_name || mgr.user_name || "").toUpperCase();
  return (
    <div
      data-testid={`traditional-header-${mgr.user_id}`}
      className="flex flex-col items-center justify-center gap-0.5"
      style={{
        background: "linear-gradient(#1B2C4A, #0E1A2E)",
        height: HEADER_HEIGHT,
        borderLeft: "1px solid rgba(0,0,0,0.35)",
        padding: "4px 6px",
      }}
    >
      <div
        className="uppercase w-full text-center"
        style={{
          fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
          color: "#F4C430",
          fontSize: 28,
          lineHeight: 1.05,
          letterSpacing: "0.04em",
          maxHeight: 58,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "normal",
          wordBreak: "keep-all",
          hyphens: "none",
        }}
      >
        {name}
      </div>
      {showBudget && (
        <div
          className="text-center"
          style={{
            fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
            color: "#FFFFFF",
            fontSize: 16,
            letterSpacing: "0.04em",
          }}
        >
          ${mgr.budget ?? 0}
        </div>
      )}
    </div>
  );
}

// Paper texture SVG noise pattern - inline data URI so no asset request.
const PAPER_NOISE_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'>
    <filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' /></filter>
    <rect width='100%' height='100%' filter='url(#n)' opacity='0.05'/>
  </svg>`,
);

export default function TraditionalDraftBoard({ orderedManagers, rounds, getCell, isDark, showBid = true, showBudget = true }) {
  const paperBase = isDark ? "#3A3226" : "#E8DEC5";

  // §B.4 - Snapshot the set of sale IDs present at first render. Any
  // sale ID NOT in this set is a new pick that arrived after mount and
  // should animate per §D.9. Existing picks render in final state.
  const initialSaleIdsRef = useRef(null);
  if (initialSaleIdsRef.current === null) {
    const ids = new Set();
    for (const mgr of orderedManagers) {
      for (let r = 0; r < rounds.length; r++) {
        const cell = getCell(mgr.user_id, r);
        if (cell) {
          ids.add(String(cell.request_id || cell.nomination_id || cell.sale_id || cell.player?.id || ""));
        }
      }
    }
    initialSaleIdsRef.current = ids;
  }
  const shouldAnimateSale = (sale) => {
    const id = String(sale?.request_id || sale?.nomination_id || sale?.sale_id || sale?.player?.id || "");
    return !initialSaleIdsRef.current.has(id);
  };

  return (
    <div
      data-testid="traditional-draft-board"
      className="w-full"
      style={{
        background: `${paperBase} url("data:image/svg+xml,${PAPER_NOISE_SVG}") repeat`,
        paddingBottom: 4,
      }}
    >
      {/* Header row - one cell per manager, navy gradient, yellow team name.
          [Snake Board Fixes item 1] Sticky within the board's scroll pane so
          team identity stays pinned while only the sticker rows scroll. This
          intentionally supersedes the older §D.3 "NOT sticky per verdict
          3.A" rule - the founder's lock-top-chrome directive covers ALL
          board styles. */}
      <div
        data-testid="traditional-header-row"
        className="grid sticky top-0 z-20"
        style={{
          gridTemplateColumns: `32px repeat(${orderedManagers.length}, minmax(90px, 1fr))`,
        }}
      >
        <div style={{ background: "linear-gradient(#1B2C4A, #0E1A2E)", height: HEADER_HEIGHT }} />
        {orderedManagers.map((mgr) => (
          <TraditionalHeaderCell key={`th-${mgr.user_id}`} mgr={mgr} showBudget={showBudget} />
        ))}
      </div>

      {/* Sticker grid - tight packing (1px gap max per §D.4). */}
      <div
        data-testid="traditional-sticker-grid"
        className="grid"
        style={{
          gridTemplateColumns: `32px repeat(${orderedManagers.length}, minmax(90px, 1fr))`,
          gap: 1,
        }}
      >
        {rounds.map((roundNum, roundIdx) => (
          <React.Fragment key={`trad-row-${roundNum}`}>
            <div
              className="flex items-center justify-center"
              style={{
                color: "rgba(0,0,0,0.55)",
                fontSize: 11,
                fontFamily: "'Bebas Neue', 'Oswald', 'Impact', sans-serif",
                background: "rgba(0,0,0,0.04)",
              }}
            >
              {roundNum}
            </div>
            {orderedManagers.map((mgr) => {
              const cell = getCell(mgr.user_id, roundIdx);
              return (
                <div
                  key={`trad-${mgr.user_id}-r${roundNum}`}
                  style={{ position: "relative", minHeight: CELL_MIN_HEIGHT }}
                >
                  {cell ? (
                    <TraditionalSoldCell sale={cell} isDark={isDark} shouldAnimate={shouldAnimateSale(cell)} showBid={showBid} />
                  ) : (
                    <TraditionalEmptyCell isDark={isDark} />
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
